import React, { createContext, useContext, useState, useEffect } from 'react';
import { useAuth } from './AuthContext';
import { Database, ChildInfo } from '../services/Database';
import { getLocalStorageItem, setLocalStorageItem, removeLocalStorageItem } from '../utils/localStorageUtils';

const DEFAULT_COLORS = ['#6B8EDB', '#E8A0C8', '#14B8A6', '#F59E0B', '#EC4899'];

interface ChildContextType {
    children: ChildInfo[];
    selectedChild: ChildInfo | null;
    selectedChildId: string | null;
    setSelectedChildId: (id: string | null) => void;
    setChildren: (children: ChildInfo[]) => void;
    refreshChildren: () => Promise<ChildInfo[]>;
    loading: boolean;
}

const ChildContext = createContext<ChildContextType>({
    children: [],
    selectedChild: null,
    selectedChildId: null,
    setSelectedChildId: () => { },
    setChildren: () => { },
    refreshChildren: async () => [],
    loading: true,
});

export const ChildProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
    const { currentUser } = useAuth();
    const [childrenList, setChildrenList] = useState<ChildInfo[]>([]);
    const [selectedChildId, setSelectedChildId] = useState<string | null>(null);
    const [loading, setLoading] = useState(true);

    const selectedChild = selectedChildId
        ? childrenList.find(c => c.id === selectedChildId) ?? null
        : null;

    const refreshChildren = async (): Promise<ChildInfo[]> => {
        if (!currentUser) return [];
        try {
            const settings = await Database.getUserSettings();
            const list = settings?.children || [];
            setChildrenList(list);
            if (list.length > 0 && (!selectedChildId || !list.some(c => c.id === selectedChildId))) {
                setSelectedChildId(list[0].id);
            } else if (list.length === 0) {
                setSelectedChildId(null);
            }
            return list;
        } catch (e) {
            console.error('刷新兒童列表失敗:', e);
            throw e;
        }
    };

    useEffect(() => {
        const fetchData = async () => {
            if (!currentUser) {
                setChildrenList([]);
                setSelectedChildId(null);
                setLoading(false);
                return;
            }
            // 登入後立即設為 loading，避免 Dashboard 在 fetch 完成前誤判「沒有兒童」而彈出新增視窗
            setLoading(true);
            try {
                const savedId = getLocalStorageItem('selectedChildId', currentUser.uid);
                const settings = await Database.getUserSettings();
                const list = settings?.children || [];
                setChildrenList(list);

                if (list.length > 0) {
                    const id = savedId && list.some(c => c.id === savedId) ? savedId : list[0].id;
                    setSelectedChildId(id);
                    setLocalStorageItem('selectedChildId', id, currentUser.uid);
                } else {
                    setSelectedChildId(null);
                    removeLocalStorageItem('selectedChildId', currentUser.uid);
                }
            } catch (e) {
                console.error('獲取兒童列表失敗:', e);
                setChildrenList([]);
                setSelectedChildId(null);
            } finally {
                setLoading(false);
            }
        };
        fetchData();
    }, [currentUser]);

    useEffect(() => {
        if (!currentUser || loading) return;
        if (selectedChildId) {
            setLocalStorageItem('selectedChildId', selectedChildId, currentUser.uid);
        } else {
            removeLocalStorageItem('selectedChildId', currentUser.uid);
        }
    }, [selectedChildId, currentUser, loading]);

    const value = {
        children: childrenList,
        selectedChild,
        selectedChildId,
        setSelectedChildId,
        setChildren: setChildrenList,
        refreshChildren,
        loading,
    };

    return (
        <ChildContext.Provider value={value}>
            {children}
        </ChildContext.Provider>
    );
};

export const useChild = () => useContext(ChildContext);

export { DEFAULT_COLORS };
